import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import type { ReactNode } from "react";
import {
  authStatusActionsSx,
  authStatusContentSx,
  authStatusHelperSx,
  authStatusIconSx,
  authStatusPageSx,
  authStatusPrimaryButtonSx,
  authStatusProgressSx,
  authStatusSecondaryButtonSx,
  getAuthStatusCardSx,
} from "./authStatusStyles";

interface AuthStatusAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  icon?: ReactNode;
  variant?: "primary" | "secondary";
}

interface AuthStatusCardProps {
  icon: ReactNode;
  title: string;
  message?: ReactNode;
  progressLabel?: string;
  warning?: ReactNode;
  helperText?: ReactNode;
  actions?: AuthStatusAction[];
}

export default function AuthStatusCard({
  icon,
  title,
  message,
  progressLabel,
  warning,
  helperText,
  actions = [],
}: AuthStatusCardProps) {
  return (
    <Box sx={authStatusPageSx}>
      <Paper elevation={0} sx={getAuthStatusCardSx({ textAlign: "center" })}>
        <Stack spacing={3} sx={authStatusContentSx}>
          <Stack spacing={1.5} alignItems="center">
            <Box sx={authStatusIconSx}>{icon}</Box>
            <Typography variant="h5">{title}</Typography>
            {message && (
              <Typography variant="body2" color="text.secondary" sx={authStatusHelperSx}>
                {message}
              </Typography>
            )}
          </Stack>

          {warning && <Alert severity="warning">{warning}</Alert>}

          {progressLabel && (
            <Stack direction="row" spacing={1.25} sx={authStatusProgressSx}>
              <CircularProgress size={18} thickness={4} />
              <Typography variant="body2" color="text.secondary">
                {progressLabel}
              </Typography>
            </Stack>
          )}

          {actions.length > 0 && (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={authStatusActionsSx}>
              {actions.map((action) => (
                <Button
                  key={action.label}
                  variant={action.variant === "secondary" ? "outlined" : "contained"}
                  size="large"
                  startIcon={action.icon}
                  sx={action.variant === "secondary" ? authStatusSecondaryButtonSx : authStatusPrimaryButtonSx}
                  onClick={action.onClick}
                  disabled={action.disabled}
                >
                  {action.label}
                </Button>
              ))}
            </Stack>
          )}

          {helperText && (
            <Typography variant="caption" color="text.secondary" sx={authStatusHelperSx}>
              {helperText}
            </Typography>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
